import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const usePlanChange = (plans, profileData, onPlanChange) => {
  const navigate = useNavigate();
  const [changePlanDrawerOpen, setChangePlanDrawerOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedOptionalCategories, setSelectedOptionalCategories] = useState(
    [],
  );
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState(null);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);

  const getActivePlan = useCallback(() => {
  return (profileData?.subscriptionPlan || []).find(
    (p) => p.status === "1" || p.status === "active" || p.status === "Active"
  );
}, [profileData]);

  const getEnabledOptionalIds = useCallback(() => {
    return (profileData?.service_category || [])
      .filter((cat) => cat.is_optional === 1 && cat.isActive === true)
      .map((cat) => cat.service_category_id);
  }, [profileData]);

  // ---- Helper: get enabled primary (mandatory) category ID ----
  const getEnabledPrimaryId = useCallback(() => {
    const primaryCat = (profileData?.service_category || []).find(
      (cat) => cat.is_optional === 0 && cat.isActive === true,
    );
    return primaryCat?.service_category_id ?? null;
  }, [profileData]);

  // ---- shouldShowDowngrade uses the active plan ----
  const shouldShowDowngrade = useCallback(
    (planType) => {
      const activePlan = getActivePlan();
      const currentPlanType = activePlan?.plan_type || 1;
      return planType < currentPlanType;
    },
    [getActivePlan],
  );

  const handleUpgradeDowngrade = useCallback(
    (plan, isUpdate = false) => {
      const planCats = plan.plan_category || [];

      const mandatoryFlagged = planCats.filter((cat) => cat.is_optional === 0);
      const primaryPairCandidates = planCats.filter((cat) => {
        const name = cat.service_category_name?.toLowerCase() || "";
        return (
          name.includes("groom") ||
          name.includes("board") ||
          name.includes("resort")
        );
      });
      const mandatoryCandidates =
        mandatoryFlagged.length > 1
          ? mandatoryFlagged
          : mandatoryFlagged.length === 0 && primaryPairCandidates.length > 1
            ? primaryPairCandidates
            : mandatoryFlagged;

      if (isUpdate) {
        setSelectedPlan({ ...plan, _isUpdate: true });

        const activePrimaryId = getEnabledPrimaryId();
        const activeOptionalIds = getEnabledOptionalIds();

        if (mandatoryCandidates.length === 1) {
          setSelectedPrimaryCategory(
            mandatoryCandidates[0].service_category_id,
          );
        } else if (mandatoryCandidates.length > 1) {
          setSelectedPrimaryCategory(
            activePrimaryId || mandatoryCandidates[0].service_category_id,
          );
        } else {
          setSelectedPrimaryCategory(null);
        }
        setSelectedOptionalCategories(activeOptionalIds);
        setShowUpgradeConfirm(true);
        return;
      }

      setSelectedPlan(plan);
      setSelectedOptionalCategories([]);

      if (mandatoryCandidates.length === 1) {
        setSelectedPrimaryCategory(mandatoryCandidates[0].service_category_id);
      } else if (mandatoryCandidates.length > 1) {
        setSelectedPrimaryCategory(mandatoryCandidates[0].service_category_id);
      } else {
        setSelectedPrimaryCategory(null);
      }

      if (shouldShowDowngrade(plan.plan_type)) {
        setShowDowngradeConfirm(true);
      } else {
        setShowUpgradeConfirm(true);
      }
    },
    [shouldShowDowngrade, getEnabledPrimaryId, getEnabledOptionalIds],
  );

  const handleConfirmPlanChange = useCallback(
    async (plan, reason, primaryId, optionalIds) => {
      try {
        await onPlanChange(plan, reason, primaryId, optionalIds);
        setShowUpgradeConfirm(false);
        setShowDowngradeConfirm(false);
        setChangePlanDrawerOpen(false);
        navigate("/dashboard");
      } catch (error) {
        // error already handled in onPlanChange
      }
    },
    [onPlanChange, navigate],
  );

  const getActivePlanInfo = useCallback(() => {
    const active = getActivePlan();
    return {
      planName: active?.plan_name,
      activePlan: active,
    };
  }, [getActivePlan]);

  return {
    changePlanDrawerOpen,
    setChangePlanDrawerOpen,
    selectedPlan,
    selectedOptionalCategories,
    setSelectedOptionalCategories,
    selectedPrimaryCategory,
    setSelectedPrimaryCategory,
    showUpgradeConfirm,
    showDowngradeConfirm,
    setShowUpgradeConfirm,
    setShowDowngradeConfirm,
    handleUpgradeDowngrade,
    handleConfirmPlanChange,
    getActivePlanInfo,
    shouldShowDowngrade,
  };
};
